import React, { useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { Coupon, CouponResult, couponsApi } from '../services/couponsService';
import BetEventPanel from './BetEventPanel';
import Modal from './Modal';

interface CouponRowProps {
  coupon: Coupon;
  onRemoved: (id: number) => void;
}

const CouponRow: React.FC<CouponRowProps> = ({ coupon, onRemoved }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalOdds = coupon.odds ?? 1;
  const eventsCount = coupon.events ?? 0;
  const first = coupon.first_event_date ?? null;
  const last = coupon.last_event_date ?? null;

  const handleRowClick = () => {
    setIsModalOpen(true);
  };

  const handleRowSurfaceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.coupon-row__actions')) return;
    handleRowClick();
  };

  const handleRowSurfaceKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if ((e.target as HTMLElement).closest('.coupon-row__actions')) return;
    e.preventDefault();
    handleRowClick();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleLockToggle = () => {
    // No-op for view-only mode
  };

  const openConfirm = () => {
    setDeleteError(null);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (!deleting) setConfirmOpen(false);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError(null);
      await couponsApi.deleteCoupon(coupon.id);
      setConfirmOpen(false);
      setIsModalOpen(false);
      onRemoved(coupon.id);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t.myParlays.deleteParlayFailed);
    } finally {
      setDeleting(false);
    }
  };

  const cr = t.myParlays?.couponResult;
  const couponResultLabel = (r: CouponResult | null | undefined): string | null => {
    if (!r) return null;
    if (r === 'WON') return cr.WON;
    if (r === 'LOST') return cr.LOST;
    if (r === 'PENDING') return cr.PENDING;
    if (r === 'VOID') return cr.VOID;
    return null;
  };

  const couponResultModifier = (r: CouponResult | null | undefined): string => {
    if (r === 'WON') return 'won';
    if (r === 'LOST') return 'lost';
    if (r === 'PENDING') return 'pending';
    if (r === 'VOID') return 'void';
    return 'none';
  };

  const aggregateLabel = couponResultLabel(coupon.result);
  const canDelete = coupon.can_delete === true;

  return (
    <>
      <div
        className="coupon-row"
        onClick={handleRowSurfaceClick}
        onKeyDown={handleRowSurfaceKeyDown}
        tabIndex={0}
        aria-label={coupon.name}
      >
        <div className="coupon-row__body">
          <div className="coupon-row__name">
            <span className="coupon-row__title">{coupon.name}</span>
            {aggregateLabel && (
              <span
                className={`coupon-row__result coupon-row__result--${couponResultModifier(coupon.result)}`}
              >
                {aggregateLabel}
              </span>
            )}
          </div>
          <div className="coupon-row__meta">
            <span className="coupon-row__odds">
              <span className="coupon-row__odds-label">{t.parlaySummary.odds}</span>
              <span className="coupon-row__odds-value">{totalOdds.toFixed(2)}</span>
            </span>
            <span className="coupon-row__events-count">
              {eventsCount} {eventsCount === 1 ? t.myParlays.event : t.myParlays.events}
            </span>
            {first && last && (
              <span className="coupon-row__dates">
                <span className="coupon-row__date">{formatDate(first)}</span>
                <span className="coupon-row__date-separator">–</span>
                <span className="coupon-row__date">{formatDate(last)}</span>
              </span>
            )}
          </div>
        </div>
        {canDelete && (
          <div className="coupon-row__actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="coupon-row__action-btn coupon-row__action-btn--delete"
              onClick={() => openConfirm()}
              title={t.common.delete}
            >
              {t.common.delete}
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleModalClose} title={coupon.name} size="large">
        <div className="coupon-details-modal">
          {coupon.bet_events && coupon.bet_events.length > 0 ? (
            <div className="coupon-details-modal__events">
              {coupon.bet_events.map((betEventOnCoupon) => {
                const betEvent = betEventOnCoupon.bet_event;
                if (!betEvent) return null;

                return (
                  <BetEventPanel
                    key={betEventOnCoupon.id}
                    betEvent={betEvent}
                    isLocked={false}
                    onLockToggle={handleLockToggle}
                    disabled={true}
                    showLegResult
                  />
                );
              })}
            </div>
          ) : (
            <div className="coupon-details-modal__empty">
              <p>{t.myParlaysExtras.noEventsFound}</p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={confirmOpen}
        onClose={closeConfirm}
        title={t.common.delete}
        size="small"
      >
        <div className="coupon-delete-confirm">
          <p className="coupon-delete-confirm__text">{t.myParlays.deleteParlayConfirm}</p>
          {deleteError && <p className="coupon-delete-confirm__error">{deleteError}</p>}
          <div className="coupon-delete-confirm__actions">
            <button
              type="button"
              className="button_secondary"
              onClick={closeConfirm}
              disabled={deleting}
            >
              {t.common.cancel}
            </button>
            <button
              type="button"
              className="coupon-delete-confirm__submit"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? t.common.loading : t.common.delete}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CouponRow;
